import Cookies, { CookieAttributes } from 'js-cookie';
import React, {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { useMediaQuery } from '../../hooks/useMediaQuery';
import { getCookieDomain } from '../../lib/getCookieDomain';
import { defaultDarkTheme } from './darkTheme';
import { defaultLightTheme } from './lightTheme';
import { getThemeCSS } from './theme';
import type { Theme, ThemeColors, ThemeOption } from './types';
import { useCustomTheme } from './useCustomTheme';

export const DARK_THEME_CLASS = 'theme-dark';
export const LIGHT_THEME_CLASS = 'theme-light';

const COOKIE_KEY_DEPRECATED = 'theme';
const COOKIE_KEY = `__Host-${COOKIE_KEY_DEPRECATED}`;

interface Props {
  colors?: ThemeColors;
  darkColors?: ThemeColors;
}

export interface ThemeProviderValue extends Theme {
  currentTheme: ThemeOption;
  isDarkMode: boolean;
  lightColors: ThemeColors;
  darkColors: ThemeColors;
  setDarkColors(colors: ThemeColors): void;
  setLightColors(colors: ThemeColors): void;
  setTheme(theme: ThemeOption): void;
}

export const ThemeContext = createContext<ThemeProviderValue>({
  ...defaultLightTheme,
  lightColors: defaultLightTheme.colors,
  darkColors: defaultDarkTheme.colors,
  currentTheme: 'user',
  isDarkMode: false,
  setDarkColors: () => {},
  setLightColors: () => {},
  setTheme: () => {},
});

export const ThemeProvider: React.FC<React.PropsWithChildren<Props>> = ({
  children,
  colors = defaultLightTheme.colors,
  darkColors = defaultDarkTheme.colors,
}) => {
  const prefersDarkMode = useMediaQuery('(prefers-color-scheme: dark)');
  const [currentTheme, setCurrentTheme] = useState<ThemeOption>('user');
  const [currentLightColors, setCurrentLightColors] = useState(colors);
  const [currentDarkColors, setCurrentDarkColors] = useState(darkColors);

  useEffect(() => {
    setCurrentLightColors(colors);
  }, [colors]);
  useEffect(() => {
    setCurrentDarkColors(darkColors);
  }, [darkColors]);

  const isDarkMode = useMemo(
    () =>
      currentTheme === 'dark' || (currentTheme === 'user' && prefersDarkMode),
    [currentTheme, prefersDarkMode]
  );

  const customLightTheme = useCustomTheme(currentLightColors);
  const customDarkTheme = useCustomTheme(currentDarkColors);

  const lightThemeCSS = getThemeCSS(customLightTheme.colors);
  const darkThemeCSS = getThemeCSS(customDarkTheme.colors);

  useEffect(() => {
    let theme = Cookies.get(COOKIE_KEY) as ThemeOption;
    if (theme) {
      setCurrentTheme(theme);
      return;
    }
    theme = Cookies.get(COOKIE_KEY_DEPRECATED) as ThemeOption;
    if (theme) {
      // replace the old cookie with the new one
      Cookies.remove(COOKIE_KEY_DEPRECATED, {
        sameSite: 'none',
        secure: true,
      });
      let cookieDomain = getCookieDomain(window.location.hostname);
      const cookieSettings: CookieAttributes = {
        sameSite: 'none',
        secure: true,
        partitioned: true,
        domain: cookieDomain,
      };
      if (cookieDomain === window.location.hostname) {
        delete cookieSettings.domain;
      }
      Cookies.set(COOKIE_KEY, theme, cookieSettings);
      setCurrentTheme(theme);
    } else {
      setCurrentTheme('user');
    }
  }, [prefersDarkMode]);

  useEffect(() => {
    document.documentElement.classList.remove(
      LIGHT_THEME_CLASS,
      DARK_THEME_CLASS
    );
    if (currentTheme !== 'user') {
      document.documentElement.classList.add(
        currentTheme === 'dark' ? DARK_THEME_CLASS : LIGHT_THEME_CLASS
      );
    }
  }, [currentTheme]);

  const setTheme = useCallback((value: ThemeOption) => {
    const cookieDomain = getCookieDomain(window.location.hostname);
    const cookieSettings: CookieAttributes = {
      sameSite: 'none',
      secure: true,
      partitioned: true,
      domain: cookieDomain,
    };
    if (cookieDomain === window.location.hostname) {
      delete cookieSettings.domain;
    }
    switch (value) {
      case 'dark':
        setCurrentTheme('dark');
        Cookies.set('theme', value, cookieSettings);
        document.documentElement.classList.add(DARK_THEME_CLASS);
        document.documentElement.classList.remove(LIGHT_THEME_CLASS);
        break;
      case 'light':
        setCurrentTheme('light');
        Cookies.set('theme', value, cookieSettings);
        document.documentElement.classList.add(LIGHT_THEME_CLASS);
        document.documentElement.classList.remove(DARK_THEME_CLASS);
        break;
      case 'user':
        setCurrentTheme('user');
        Cookies.remove('theme', cookieSettings);
        document.documentElement.classList.remove(
          DARK_THEME_CLASS,
          LIGHT_THEME_CLASS
        );
        break;
    }
  }, []);

  return (
    <ThemeContext.Provider
      value={{
        ...(isDarkMode ? customDarkTheme : customLightTheme),
        lightColors: customLightTheme.colors,
        darkColors: customDarkTheme.colors,
        currentTheme,
        isDarkMode,
        setDarkColors: setCurrentDarkColors,
        setLightColors: setCurrentLightColors,
        setTheme,
      }}
    >
      {children}
      <style jsx global>{`
        :root,
        .theme-light {
          ${lightThemeCSS}

          color: var(--text-default);
        }
        .theme-dark {
          ${darkThemeCSS}

          color: var(--text-default);
        }
        @media (prefers-color-scheme: light) {
          :root {
            ${lightThemeCSS}

            color: var(--text-default);
          }
          .theme-dark {
            ${darkThemeCSS}

            color: var(--text-default);
          }
        }
        @media (prefers-color-scheme: dark) {
          :root {
            ${darkThemeCSS}

            color: var(--text-default);
          }
          .theme-light {
            ${lightThemeCSS}

            color: var(--text-default);
          }
        }
      `}</style>
    </ThemeContext.Provider>
  );
};
